// VandieZ admin-web React entry. Mounts <App /> under #root with StrictMode.
//
// Loaded by index.html via <script type="module" src="/src/main.tsx">.
// Vite handles JSX/TS transpilation and HMR injection.

import * as Sentry from "@sentry/react";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./App";

import "./index.css";

// ---------------------------------------------------------------------------
// Sentry init
//
// Init runs at module import time so render-phase crashes are captured.
// SDK is a no-op when DSN is empty (default in dev / when DSN not yet
// provisioned), so unconditional init is safe.
//
// PII posture mirrors the backend (`app/core/logging.sentry_before_send`):
// drop Authorization header outright, mask known PII field names, never
// auto-attach IP/cookies/form data via `sendDefaultPii: false`.
// ---------------------------------------------------------------------------
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const sentryEnvironment =
  (import.meta.env.VITE_SENTRY_ENVIRONMENT) ??
  "development";

const PII_FIELDS = new Set([
  "phone",
  "mobile",
  "mobile_number",
  "phone_number",
  "email",
  "email_address",
  "aadhaar",
  "aadhaar_number",
  "pan",
  "pan_number",
  "account_number",
  "bank_account",
  "ifsc",
  "ifsc_code",
  "password",
  "passwd",
  "secret",
  "token",
  "access_token",
  "refresh_token",
  "api_key",
  "jwt_secret",
  "otp",
  "otp_code",
]);

function maskValue(value: string): string {
  if (value.length <= 4) {return "****";}
  return "*".repeat(value.length - 4) + value.slice(-4);
}

function scrubObject(obj: unknown): void {
  if (typeof obj !== "object" || obj === null) {return;}
  const record = obj as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const value = record[key];
    if (PII_FIELDS.has(key.toLowerCase()) && typeof value === "string" && value) {
      record[key] = maskValue(value);
    } else if (typeof value === "object" && value !== null) {
      scrubObject(value);
    }
  }
}

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: sentryEnvironment,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    beforeSend(event) {
      const headers = event.request?.headers as
        | Record<string, string>
        | undefined;
      if (headers) {
        delete headers["Authorization"];
        delete headers["authorization"];
        scrubObject(headers);
      }
      if (event.request?.data) {scrubObject(event.request.data);}
      if (event.extra) {scrubObject(event.extra);}
      return event;
    },
  });
}

const rootElement = document.getElementById("root");
if (rootElement === null) {
  throw new Error("Root element #root not found in index.html");
}

createRoot(rootElement).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <div style={{ padding: 24, fontFamily: "sans-serif" }}>
          <h2>Something went wrong</h2>
          <pre style={{ whiteSpace: "pre-wrap" }}>
            {error instanceof Error ? error.message : String(error)}
          </pre>
        </div>
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
